import React from 'react';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import PropertyList from '@dashboard/components/PropertyList';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import Dash from '@/dashboard/components/Dash';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/dashboard/context/ToastContext';
import { formatDateTime } from '@/dashboard/lib/format-date';
import { formatPrice } from '@/dashboard/services/formatting';
import Badge from '@/dashboard/components/Badge';
import getStripeDashboardUrl from '@/dashboard/lib/stripe/get-stripe-dashboard-url';
import Link from '@/dashboard/components/Link';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import PageMultiSelectPageContainer from '@/dashboard/components/Formik/components/PageMultiSelectPageContainer';

function openDashboardLink(pageId) {
  window.open(`/dashboard/pages/${pageId}/customize/product`, '_blank');
}

const DiscountsCouponsDetail = ({ coupon, ticketTypesByPage = [] }) => {
  const { seller, isAdminLoggedInAsSeller } = useAuth();
  const { emitToast } = useToast();

  const discountFields = coupon.percentOff
    ? {
        Discount: `${coupon.percentOff}%`,
      }
    : coupon.amountOff
      ? {
          Amount: formatPrice({
            amount: coupon.amountOff,
            currency: coupon.currency,
          }),
          Currency: coupon.currency?.toUpperCase(),
        }
      : {};

  const couponDetails = {
    Label: coupon.label || <Dash />,
    Code: (
      <div className="flex flex-row">
        <span className="mr-2 mb-auto">{coupon.code || <Dash />}</span>
        <CopyToClipboard
          text={coupon.code}
          onCopy={() => {
            emitToast({
              title: 'Copied to clipboard',
              status: 'success',
            });
          }}
        >
          <Button appearance="icon-button" style={{ padding: 0 }}>
            <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />
          </Button>
        </CopyToClipboard>
      </div>
    ),
    ...(coupon.type
      ? [
          {
            'Page type':
              coupon.type === 'charge' ? (
                'One-off payments'
              ) : coupon.type === 'subscription' ? (
                'Subscriptions'
              ) : (
                <Badge appearance="info" className="ml-2">
                  Legacy
                </Badge>
              ),
          },
        ]
      : []),
    ...(coupon.type
      ? [
          {
            '': 'This is a legacy coupon that only works on one-time payment links. To use this coupon for both one-time and subscription payments, please recreate it.',
          },
        ]
      : []),
    Redemptions:
      coupon.timesRedeemed !== undefined ? (
        `${coupon.timesRedeemed} / ${coupon.maxRedemptions != null ? coupon.maxRedemptions : '∞'}`
      ) : (
        <Dash />
      ),
    ...discountFields,
    'Applies to setup fee': coupon.appliesToSetupFee ? 'Yes' : 'No',
    __durationLabel: (
      <>
        Subscription duration{' '}
        <InfoTooltip>On subscriptions, the discount will be applied for this duration.</InfoTooltip>
      </>
    ),
    'Max uses': coupon.maxRedemptions ? coupon.maxRedemptions : '∞',
    'Expires on': coupon.redeemBy ? formatDateTime(coupon.redeemBy) : <Dash />,
    'Subscription duration':
      coupon.duration === 'forever' ? (
        'Every payment'
      ) : coupon.duration === 'once' ? (
        'First payment'
      ) : coupon.duration === 'repeating' ? (
        `Multiple payments (${coupon.durationInMonths} months)`
      ) : (
        <Dash />
      ),
    'Stripe coupon ID': (
      <Link
        className="font-normal"
        to={getStripeDashboardUrl(`/coupons/${coupon.stripeId}`, {
          seller,
          isAdminLoggedInAsSeller,
        })}
        blank
        external
      >
        {coupon.stripeId}
      </Link>
    ),
    'Created on': coupon.createdAt ? formatDateTime(coupon.createdAt) : <Dash />,
  };

  return (
    <>
      <div className=" w-full px-4 pt-4">
        <PropertyList.Title>Details</PropertyList.Title>

        {Object.entries(couponDetails).map(([key, value], index) => {
          /**
           * Duration label contains jsx this is a workaround.
           */
          if (key === '__durationLabel') {
            return null;
          }

          const label = key === 'Subscription duration' ? couponDetails.__durationLabel : key;

          return (
            <PropertyList.Item key={index}>
              <PropertyList.ItemLabel size="narrow">{label}</PropertyList.ItemLabel>
              <PropertyList.ItemValue size="narrow">{value}</PropertyList.ItemValue>
            </PropertyList.Item>
          );
        })}
      </div>

      {coupon.pages && coupon.pages.length > 0 && (
        <Card>
          <CardHeader>
            <CardHeaderTitle>Limit to pages</CardHeaderTitle>
          </CardHeader>
          <CardContent>
            <div className="max-w-lg">
              {coupon.pages.map((page, index, array) => {
                if (seller?.labs?.couponTicketTypes)
                  return (
                    <PageMultiSelectPageContainer
                      key={page._id}
                      page={page}
                      isLast={index === array.length - 1}
                      onVisit={openDashboardLink}
                      showRemoveButton={false}
                      includeTicketTypes={
                        page.type === 'event' &&
                        ticketTypesByPage[page._id]?.ticketTypes?.length > 0
                      }
                      isReadOnly={true}
                      directTicketTypes={ticketTypesByPage[page._id]?.ticketTypes || []}
                    />
                  );

                return (
                  <PageMultiSelectPageContainer
                    key={page._id}
                    page={page}
                    isLast={index === array.length - 1}
                    onVisit={openDashboardLink}
                    showRemoveButton={false}
                    includeTicketTypes={false}
                    isReadOnly={true}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DiscountsCouponsDetail;
